import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { Plus, Clock, MapPin, CalendarDays, TrendingUp, ExternalLink } from "lucide-react";
import { openInMaps, effectiveTotal } from "@/lib/jobActions";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const DAYS_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const statusPill = {
  scheduled: "bg-primary/15 text-primary ring-primary/40",
  completed: "bg-emerald-500/15 text-emerald-400 ring-emerald-500/40",
  cancelled: "bg-destructive/15 text-destructive ring-destructive/40",
};

export default function DayJobsDialog({ open, onOpenChange, date, jobs, onSelectJob, onAddJob }) {
  const { lang, t } = useLang();
  if (!date) return null;

  const d = new Date(date + "T00:00:00");
  const isValid = !isNaN(d.getTime());
  const DAYS = lang === "es" ? DAYS_ES : DAYS_EN;
  const MONTHS = lang === "es" ? MONTHS_ES : MONTHS_EN;
  const dayName = isValid ? DAYS[d.getDay()] : "";
  const dateLong = isValid ? `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : date;

  const list = (jobs || []).slice().sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const totalIncome = list.reduce((s, j) => s + (j.status === "cancelled" ? 0 : effectiveTotal(j)), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto bg-card border-border p-0"
        data-testid="day-jobs-dialog"
      >
        {/* Header */}
        <div className="relative p-6 pb-5 border-b border-border overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-transparent" />
          <div className="relative pr-8">
            <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest font-bold">
              <CalendarDays className="h-3.5 w-3.5" /> {dayName}
            </div>
            <DialogTitle className="mt-2 text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="day-jobs-title">
              {dateLong}
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-muted-foreground">
              {list.length === 0
                ? (lang === "es" ? "Sin trabajos programados" : "No jobs scheduled")
                : `${list.length} ${list.length === 1 ? (lang === "es" ? "trabajo" : "job") : (lang === "es" ? "trabajos" : "jobs")} · ${fmt(totalIncome)} ${lang === "es" ? "ingreso esperado" : "expected income"}`}
            </DialogDescription>
          </div>
        </div>

        {/* List */}
        <div className="p-6">
          {list.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-10 text-center">
              <div className="text-sm text-muted-foreground mb-4">
                {lang === "es" ? "Este día está libre." : "This day is free."}
              </div>
              <Button
                onClick={() => { onOpenChange(false); onAddJob?.(date); }}
                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                data-testid="day-jobs-empty-add-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> {t.portal.addJob}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {list.map((j) => {
                const jc = j.status === "cancelled";
                return (
                <button
                  key={j.id}
                  onClick={() => { onOpenChange(false); onSelectJob?.(j); }}
                  className={`w-full text-left rounded-xl border transition-all p-4 group ${jc ? "border-border/50 bg-background/20 opacity-60" : "border-border bg-background/40 hover:border-primary hover:bg-background/70"}`}
                  data-testid={`day-job-item-${j.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className={`inline-flex items-center gap-1.5 text-xs font-bold ${jc ? "text-muted-foreground" : "text-primary"}`}>
                          <Clock className="h-3 w-3" /> {j.start_time} – {j.end_time}
                        </div>
                        <span className={`inline-flex items-center text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ring-1 ${statusPill[j.status] || statusPill.scheduled}`}>
                          {j.status}
                        </span>
                      </div>
                      <div className={`mt-2 font-semibold text-lg transition-colors ${jc ? "line-through text-muted-foreground" : "group-hover:text-primary"}`}>
                        {j.client_name}
                      </div>
                      <div className={`text-sm text-muted-foreground ${jc ? "line-through" : ""}`}>{j.service_type}</div>
                      {(j.address_from || j.address_to) && (
                        <div className="mt-2 text-xs text-muted-foreground flex items-start gap-1.5">
                          <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <div className="truncate">
                            {j.address_from && (
                              <span
                                role="link"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); openInMaps(j.address_from); }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); openInMaps(j.address_from); } }}
                                className="underline decoration-dotted underline-offset-4 hover:text-primary cursor-pointer"
                                data-testid={`day-job-map-from-${j.id}`}
                              >
                                {j.address_from}
                              </span>
                            )}
                            {j.address_from && j.address_to && <span className="mx-1">→</span>}
                            {j.address_to && (
                              <span
                                role="link"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); openInMaps(j.address_to); }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); openInMaps(j.address_to); } }}
                                className="underline decoration-dotted underline-offset-4 hover:text-primary cursor-pointer"
                                data-testid={`day-job-map-to-${j.id}`}
                              >
                                {j.address_to}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-xl font-bold ${jc ? "line-through text-muted-foreground" : "text-primary"}`} style={{ fontFamily: "Outfit" }}>{fmt(effectiveTotal(j))}</div>
                      {parseFloat(j.deposit) > 0 && (
                        <div className="text-[10px] text-muted-foreground">Deposit {fmt(j.deposit)}</div>
                      )}
                    </div>
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {list.length > 0 && (
          <div className="sticky bottom-0 bg-card/95 backdrop-blur border-t border-border p-4 flex items-center justify-between">
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3" /> {lang === "es" ? "Total del día" : "Day total"}: <span className="text-foreground font-bold">{fmt(totalIncome)}</span>
            </div>
            <Button
              onClick={() => { onOpenChange(false); onAddJob?.(date); }}
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              data-testid="day-jobs-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> {t.portal.addJob}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
